import React, { createContext, useContext, useState, useEffect } from 'react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';

// MSAL Configuration

const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_ENTRA_CLIENT_ID || 'your-client-id',
        authority: process.env.REACT_APP_ENTRA_AUTHORITY || 'https://login.microsoftonline.com/your-tenant-id',
        redirectUri: process.env.REACT_APP_ENTRA_REDIRECT_URI || 'http://localhost:3000',
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    },
};

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Login request
const loginRequest = {
    scopes: ['User.Read', 'openid', 'profile', 'email'],
};

// Auth Context
const EntraAuthContext = createContext();

// Custom hook to use auth context
export const useEntraAuth = () => {
    const context = useContext(EntraAuthContext);
    if (!context) {
        throw new Error('useEntraAuth must be used within an EntraAuthProvider');
    }
    return context;
};

// Auth Provider Component
export const EntraAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initialize MSAL
    useEffect(() => {
        const initializeMsal = async () => {
            try {
                // Initialize MSAL instance
                await msalInstance.initialize();

                // Handle redirect promise
                const response = await msalInstance.handleRedirectPromise();

                // If we got a response, user just authenticated
                if (response) {
                    console.log('✅ Microsoft authentication successful');
                    console.log('Response account:', response.account);

                    const userEmail = response.account.username || response.account.email || '';
                    console.log('Extracted email:', userEmail);

                    setIsAuthenticated(true);
                    setUser({
                        name: response.account.name,
                        username: response.account.username,
                        email: userEmail,
                        localAccountId: response.account.localAccountId,
                        entraRoles: response.idTokenClaims?.roles || [],
                    });
                    return; // Don't check for existing accounts if we just authenticated
                }

                // Check if user is already signed in
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    setIsAuthenticated(true);
                    setUser({
                        name: accounts[0].name,
                        username: accounts[0].username,
                        email: accounts[0].username,
                        localAccountId: accounts[0].localAccountId,
                        entraRoles: [], // Will be populated from token claims
                    });
                }
            } catch (error) {
                console.error('MSAL initialization error:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        initializeMsal();

        // Listen for auth events
        const callbackId = msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                const account = event.payload.account;
                setIsAuthenticated(true);
                setUser({
                    name: account.name,
                    username: account.username,
                    localAccountId: account.localAccountId,
                    entraRoles: event.payload.idTokenClaims?.roles || [],
                });
                setError(null);
            } else if (event.eventType === EventType.LOGIN_FAILURE) {
                setError(event.error?.message || 'Login failed');
            } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setIsAuthenticated(false);
                setUser(null);
                setError(null);
            }
        });

        return () => {
            if (callbackId) {
                msalInstance.removeEventCallback(callbackId);
            }
        };
    }, []);

    // Login function
    const login = async () => {
        try {
            setError(null);
            await msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            console.error('Login error:', error);
            setError(error.message);
        }
    };

    // Logout function
    const logout = async () => {
        try {
            await msalInstance.logoutRedirect({
                postLogoutRedirectUri: window.location.origin,
            });
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback to local logout
            setIsAuthenticated(false);
            setUser(null);
            localStorage.clear();
        }
    };

    // Get access token
    const getAccessToken = async () => {
        try {
            const account = msalInstance.getAllAccounts()[0];
            if (!account) {
                throw new Error('No account found');
            }

            const tokenResponse = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
            });

            return tokenResponse.accessToken;
        } catch (error) {
            console.error('Token acquisition error:', error);
            // Try interactive token acquisition
            try {
                const tokenResponse = await msalInstance.acquireTokenRedirect(loginRequest);
                return tokenResponse.accessToken;
            } catch (interactiveError) {
                throw interactiveError;
            }
        }
    };

    const value = {
        isAuthenticated,
        user,
        loading,
        error,
        login,
        logout,
        getAccessToken,
        msalInstance,
    };

    return (
        <EntraAuthContext.Provider value={value}>
            {children}
        </EntraAuthContext.Provider>
    );
};
